import { z } from 'zod';

/**
 * MANKEN KADROSU — "ürünü tanıtan kişi" ekseni (21 Ağu 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Bu dosya `tryon` (kıyafet deneme) DEĞİL ve onun yerine geçiyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Eski `model` sekmesi `fal-ai/flux-pro/v1/vto` ile çalışıyordu ve o ucun
 * yaptığı TEK şey var: bir insanın üstündeki kıyafeti başka bir kıyafetle
 * değiştirmek. Yani kadroyu yalnız tekstil satan bir mağaza kullanabiliyordu;
 * ayakkabı, çanta, şişe, kozmetik ya da elektronik satan biri için sekme
 * ekranda duruyor ama hiçbir işe yaramıyordu.
 *
 * Bu eksende soru başka: "ürünü BİR İNSAN TANITSIN". İnsan ürünü giyebilir,
 * elinde tutabilir, kullanabilir ya da kameraya gösterebilir — dördü de aynı
 * işin farklı fiilleri ve dördü de tek bir üretim yoluyla çözülüyor
 * (`bytedance/seedream/v5/pro/edit`, çoklu referans görsel). Bu yüzden manken
 * artık bir SEKME değil, `prompt` ve `product` sekmelerinin ikisinde de
 * bulunan bir SEÇENEK.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 HAZIR KADRO bir TABLO DEĞİL, bir KATALOG
 * ═══════════════════════════════════════════════════════════════════════════
 * İki tür manken var ve ikisi iki ayrı yerde yaşıyor — bu bilinçli:
 *
 *   · HAZIR KADRO (`PRESET_STUDIO_MODELS`) → kodda. Kiracıya ait değil,
 *     herkes aynısını görüyor, fotoğrafı depoda SABİT bir anahtarda duruyor.
 *     Tabloya konsaydı `workspaceId` kolonu NULL olurdu ve kiracı yalıtım
 *     katmanı (`prisma.service.ts`) tam olarak bunu yasaklıyor: `workspaceId`
 *     taşımayan bir satır o katmanda korunamaz. Sahne şablonları da aynı
 *     gerekçeyle katalog (`scene-templates.ts`).
 *
 *   · ÖZEL MANKEN (`StudioModel` tablosu) → veritabanında, `workspaceId`
 *     ZORUNLU. Kullanıcının kendi tarifinden üretilen ve yalnız onun gördüğü
 *     kişi. Kredi karşılığı bir kez üretiliyor, sonra tüm ürünlerinde bedava
 *     kullanılıyor — bedeli üretimde, kullanımda değil.
 *
 * İkisi arayüzde TEK bir listede görünüyor (`StudioModelCard.source` ayırıyor)
 * çünkü kullanıcının sorusu tek: "hangi kişi tanıtsın".
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu dosya HİÇBİR ŞEY import etmiyor — ve etmemeli
 * ═══════════════════════════════════════════════════════════════════════════
 * `creative.ts` buradan `presenterParamsSchema`yı DEĞER olarak alıyor.
 * `studio.ts` ise `creative.ts`ten değer alıyor. Buradan o ikisine bir import
 * çıksaydı çalışma zamanında gerçek bir döngü doğardı (gerekçe `creative.ts`
 * → `import type { StudioCostBreakdown }` notunda). Bu yüzden cinsiyet/yaş/
 * vücut listeleri de BURADA tanımlı ve `studio.ts` onları buradan okuyor —
 * ters yön değil.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Eksenler — kapalı listeler
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kapalı listeler serbest metin yerine BİLİNÇLİ: bu değerler doğrudan
 * prompt'a giriyor. Serbest metin hem tahmin edilemeyen çıktı hem de bir
 * moderasyon yüzeyi demek — "16 yaşında" ya da bir gerçek kişinin adı yazılan
 * bir tarif, üretilmemesi gereken bir görsel üretirdi. Kullanıcının serbest
 * yazabildiği tek alan `StudioModelDraft.note` ve o da moderasyondan geçiyor.
 */
export const MODEL_GENDERS = ['female', 'male', 'neutral'] as const;
export type ModelGender = (typeof MODEL_GENDERS)[number];

export const MODEL_AGE_RANGES = ['18-25', '26-35', '36-50', '50+'] as const;
export type ModelAgeRange = (typeof MODEL_AGE_RANGES)[number];

export const MODEL_BODY_TYPES = ['slim', 'average', 'curvy', 'plus'] as const;
export type ModelBodyType = (typeof MODEL_BODY_TYPES)[number];

/**
 * Ten tonu — Fitzpatrick'in kabalaştırılmış hâli.
 *
 * Adlandırma bilerek RENK değil TON: "beyaz/siyah" bir kimlik iddiası,
 * "açık/koyu" ise fotoğrafın ölçülebilir bir özelliği. Model prompt'unda da
 * bu ikinci dil çalışıyor — etnik köken adı veren bir tarif, modelin o
 * kökene dair klişesini çiziyor.
 */
export const MODEL_SKIN_TONES = ['light', 'medium', 'tan', 'deep'] as const;
export type ModelSkinTone = (typeof MODEL_SKIN_TONES)[number];

export const MODEL_HAIR_STYLES = ['short', 'medium', 'long', 'curly', 'tied', 'bald'] as const;
export type ModelHairStyle = (typeof MODEL_HAIR_STYLES)[number];

/**
 * MANKEN ÜRÜNLE NE YAPIYOR.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `auto` bir kaçamak değil, çoğu zaman DOĞRU cevap
 * ═══════════════════════════════════════════════════════════════════════════
 * Fiili ürün belirliyor: bir tişört giyilir, bir parfüm tutulur, bir fön
 * makinesi kullanılır. Kullanıcıya "bu ürün giyilir mi tutulur mu" diye
 * sormak, ona kendi ürününü tarif ettirmek olurdu — ve yanlış seçtiğinde
 * çıkan şey elinde tişört tutan bir manken oluyor.
 *
 * `auto` kipinde karar modele bırakılıyor ve prompt ona ürün adını veriyor
 * (`StudioProductSnapshot.title`). Diğer dördü, modelin yanlış karar verdiği
 * durumda kullanıcının elindeki DÜZELTME — varsayılan değil.
 */
export const PRESENTER_ACTIONS = ['auto', 'wear', 'hold', 'use', 'show'] as const;
export type PresenterAction = (typeof PRESENTER_ACTIONS)[number];

/// Mankene verilen tek cümlelik yönlendirme ("kameraya gülümsesin").
/// Kısa tutuluyor: bu alan bir brief değil, brief zaten ayrı bir alan.
export const PRESENTER_NOTE_MAX = 200;

/// Özel manken tarifinin serbest metin alanı — "kısa sakallı, gözlüklü".
export const STUDIO_MODEL_NOTE_MAX = 400;

/// Kadro adı. Kullanıcı kendi mankenine ad veriyor ("Kampanya yüzü").
export const STUDIO_MODEL_NAME_MAX = 60;

/**
 * Bir kiracının kaç özel mankeni olabilir.
 *
 * Sınır teknik değil ürünsel: kadro bir SEÇİM ekranı ve otuz kişilik bir
 * listede seçim yapmak, iki kişilik bir listede yapmaktan zor. Kredi zaten
 * bir fren ama tek fren o olsaydı, bütçesi geniş bir kiracı kendine
 * kullanamayacağı bir katalog kurardı.
 */
export const STUDIO_MODEL_MAX_PER_WORKSPACE = 12;

/**
 * ÖZEL MANKEN ÜRETİM BEDELİ — 15 kredi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `AD_IMAGE`in (4) katı, neden `CREDIT_COST_BY_CREATIVE_TYPE`'ta değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Sağlayıcı maliyeti tek bir Seedream çağrısı — yani teknik olarak bir
 * reklam görseliyle aynı. Fiyatı ayıran şey maliyet değil NE SATILDIĞI:
 * çıktı bir kreatif değil, kiracının kütüphanesinde KALICI olarak duran ve
 * bundan sonraki bütün üretimlerinde bedava kullanacağı bir varlık. Bir kez
 * ödenip sınırsız kullanılan bir şeyin, bir kez ödenip bir kez kullanılan bir
 * şeyle aynı fiyatta olması, ikincisini satın alanı cezalandırırdı.
 *
 * Tabloya girmiyor çünkü `CreativeType` üretmiyor: kütüphanede bir kreatif
 * satırı açılmıyor, `StudioModel` satırı açılıyor. `PRODUCT_VISION_CREDITS`
 * de aynı gerekçeyle tablonun dışında.
 */
export const STUDIO_MODEL_CREDITS = 15;

/**
 * Kredi defterindeki referans türü — iade bu dizeyle doğru tabloya yazılıyor.
 * `PRODUCT_VISION_REF` ile birebir aynı desen; worker'daki dal onu tanıyor.
 */
export const STUDIO_MODEL_REF = 'studio_model';

// ═══════════════════════════════════════════════════════════════════════════
// Depo anahtarları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 HAZIR KADRO FOTOĞRAFLARI `ws/<workspaceId>/` ÖNEKİNİN DIŞINDA.
 *
 * Bu bir istisna ve tek istisna. `isOwnObjectKey` istemciden gelen her
 * anahtarın kiracının önekiyle başlamasını şart koşuyor — kiracı yalıtımının
 * bu alandaki en ince noktası (gerekçe `creative.keys.ts`). Hazır kadro
 * hiçbir kiracıya ait olmadığı için o kuralı geçemez ve geçmemeli.
 *
 * Çözüm anahtarı gevşetmek değil, İKİNCİ BİR KAPI açmak: presenter alanı
 * istemciden ANAHTAR değil KİMLİK alıyor (`presenter.modelId`) ve anahtarı
 * sunucu üretiyor. Yani istemcinin bu öneke bir dize yazabildiği hiçbir yol
 * yok — kimlik katalogda yoksa istek reddediliyor.
 */
export const SYSTEM_MODEL_PREFIX = 'system/studio-models/';

export function presetModelKey(id: string): string {
  return `${SYSTEM_MODEL_PREFIX}${id}.webp`;
}

/// Özel mankenin referans fotoğrafı — kiracının kendi öneki altında.
export function customModelKey(workspaceId: string, modelId: string): string {
  return `ws/${workspaceId}/studio-models/${modelId}.webp`;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hazır kadro
// ═══════════════════════════════════════════════════════════════════════════

export interface PresetStudioModel {
  id: string;
  /// Kadroda görünen ad. Çeviri YOK: bir insanın adı dile göre değişmiyor.
  name: string;
  gender: ModelGender;
  ageRange: ModelAgeRange;
  bodyType: ModelBodyType;
  skinTone: ModelSkinTone;
  hair: ModelHairStyle;
  /// Kartın altındaki tek satır — kimi seçtiğini anlatıyor.
  description: { tr: string; en: string };
}

/**
 * BAŞLANGIÇ KADROSU — bir kadın, bir erkek (21 Ağu 2026).
 *
 * İkiyle başlamak bir kısıt değil bir ölçüm: kadro büyütülmeden önce ÇIKTI
 * KALİTESİNİN doğrulanması gerekiyor. On kişilik bir kadro kurup hepsinin
 * aynı kusuru taşıdığını sonra görmek, on kez aynı hatayı üretmek olurdu.
 *
 * ⚠️ Fotoğraflar ÜRETİLDİ, stok değil: her biri `studio-model-recipes.ts`
 * içindeki kendi reçetesiyle Seedream v5 Pro'da üretilip
 * `system/studio-models/<id>.webp` anahtarına yazıldı. Stok fotoğraf
 * kullanılmamasının sebebi hukuki: bir stok mankenin yüzü, o fotoğrafın
 * lisansının kapsamadığı bir reklamda kullanılamaz — ve kullanıcı üretilen
 * görselle reklam veriyor.
 *
 * Reçete değişirse fotoğraf da yeniden üretilmeli:
 *     node /root/plesk-ops/mentorivo/studio-model-gen.mjs <id>
 */
export const PRESET_STUDIO_MODELS: readonly PresetStudioModel[] = [
  {
    id: 'ada',
    name: 'Ada',
    gender: 'female',
    ageRange: '26-35',
    bodyType: 'average',
    skinTone: 'medium',
    hair: 'long',
    description: {
      tr: 'Nötr stüdyo ışığı, sıcak ve sakin ifade — moda ve kozmetikte güvenli seçim.',
      en: 'Neutral studio light, warm and calm expression — a safe pick for fashion and beauty.',
    },
  },
  {
    id: 'emre',
    name: 'Emre',
    gender: 'male',
    ageRange: '26-35',
    bodyType: 'average',
    skinTone: 'medium',
    hair: 'short',
    description: {
      tr: 'Sade duruş, net çene hattı — teknoloji, spor ve günlük giyimde okunaklı.',
      en: 'Plain stance, defined jawline — reads well for tech, sport and everyday wear.',
    },
  },
];

export function findPresetModel(id: string): PresetStudioModel | null {
  return PRESET_STUDIO_MODELS.find((model) => model.id === id) ?? null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Üretim isteği — presenter
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `creativeGenerateSchema.presenter` — hem `prompt` hem `product` sekmesinde.
 *
 * 🔴 ANAHTAR DEĞİL KİMLİK. Manken fotoğrafının S3 anahtarı istemciden
 * ALINMIYOR; kimlik geliyor, anahtar sunucuda çözülüyor (`SYSTEM_MODEL_PREFIX`
 * başlığındaki gerekçe). Kullanıcının kendi çektiği bir fotoğrafı manken
 * yapması ayrı bir yol: önce kütüphaneye ekleniyor (`POST /creatives/models`
 * → `sourceAssetKey`), sonra kimliğiyle seçiliyor. Böylece "bu görsel gerçekten
 * bu kiracıya mı ait" sorusu TEK yerde, ekleme anında cevaplanıyor.
 */
export const presenterParamsSchema = z.object({
  modelId: z.string().trim().min(1).max(64),
  action: z.enum(PRESENTER_ACTIONS).default('auto'),
  note: z.string().trim().max(PRESENTER_NOTE_MAX).optional(),
});
export type PresenterParams = z.infer<typeof presenterParamsSchema>;

/**
 * Worker'ın gördüğü çözülmüş hâl — `Creative.inputParams.presenter`.
 *
 * Kimlik ÜRETİM ANINDA çözülüp dondurulur: kullanıcı özel mankenini altı ay
 * sonra silse bile "bu görsel kiminle üretildi" sorusu cevaplanabilir kalır.
 * `StudioProductSnapshot`un fiyatı dondurmasıyla aynı karar, aynı gerekçe.
 */
export const resolvedPresenterSchema = z.object({
  modelId: z.string(),
  name: z.string(),
  gender: z.enum(MODEL_GENDERS),
  action: z.enum(PRESENTER_ACTIONS),
  note: z.string().nullable(),
  s3Key: z.string(),
  source: z.enum(['preset', 'custom']),
});

/**
 * ⚠️ Tip zod'dan TÜRETİLİYOR, elle yazılmıyor.
 *
 * `Creative.inputParams` bir `Json?` ve worker onu okurken doğruluyor
 * (`render.service.ts` → `readPresenter`). İki ayrı tanım (interface + şema)
 * olsaydı biri diğerinden sessizce ayrışır ve doğrulama, tipin vaat ettiği
 * bir alanı düşürürdü.
 */
export type ResolvedPresenter = z.infer<typeof resolvedPresenterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Kadro uçları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Özel manken ÜRETİMİNİN durumu.
 *
 * `JobStatus`u yeniden kullanmıyor ve bu bilinçli: o enum bir kuyruk işinin
 * durumu ve `CANCELED` değeri taşıyor — özel manken üretimi iptal edilemiyor
 * (tek bir Seedream çağrısı, saniyeler sürüyor). Ekrana çıkan küme ne kadar
 * darsa, arayüzün çizmesi gereken hâl o kadar az.
 */
export const STUDIO_MODEL_STATES = ['PENDING', 'READY', 'FAILED'] as const;
export type StudioModelState = (typeof STUDIO_MODEL_STATES)[number];

/** Arayüzün gördüğü kadro satırı — hazır ve özel mankenler AYNI biçimde. */
export interface StudioModelCard {
  id: string;
  name: string;
  gender: ModelGender;
  source: 'preset' | 'custom';
  state: StudioModelState;
  /// Kısa ömürlü imzalı URL. Hazır kadroda da imzalı: bucket public değil.
  previewUrl: string | null;
  /// Yalnız hazır kadroda dolu; özel mankende kullanıcının kendi tarifi var.
  description: { tr: string; en: string } | null;
  /// Özel mankende kullanıcının yazdığı serbest tarif — kartın altında.
  note: string | null;
  /// Ekrana çıkan i18n anahtarı; yalnız `state === 'FAILED'` iken dolu.
  errorCode: string | null;
  createdAt: string | null;
}

export interface StudioModelListResponse {
  /// Hazır kadro + kiracının özel mankenleri, TEK liste.
  models: StudioModelCard[];
  /// Kaç özel manken daha eklenebilir (`STUDIO_MODEL_MAX_PER_WORKSPACE` eksi mevcut).
  remainingSlots: number;
  credits: number;
}

/**
 * ÖZEL MANKEN İSTEĞİ.
 *
 * İki yol var ve TAM OLARAK BİRİ dolu olmalı:
 *   · `brief` → tarif edilen kişi ÜRETİLİYOR (kredi yanıyor).
 *   · `sourceAssetKey` → kullanıcının yüklediği fotoğraf olduğu gibi
 *     kadroya giriyor (kredi YOK — üretim yapılmıyor).
 *
 * İkincisinin kredisiz olması bir cömertlik değil bir tanım: ortalıkta
 * sağlayıcı çağrısı yok, yalnızca bir kopyalama var. Ücretlendirmek,
 * kullanıcıdan kendi dosyasını depolamak için para almak olurdu.
 */
export const studioModelCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(STUDIO_MODEL_NAME_MAX),
    brief: z
      .object({
        gender: z.enum(MODEL_GENDERS).default('female'),
        ageRange: z.enum(MODEL_AGE_RANGES).default('26-35'),
        bodyType: z.enum(MODEL_BODY_TYPES).default('average'),
        skinTone: z.enum(MODEL_SKIN_TONES).default('medium'),
        hair: z.enum(MODEL_HAIR_STYLES).default('short'),
        note: z.string().trim().max(STUDIO_MODEL_NOTE_MAX).optional(),
      })
      .optional(),
    sourceAssetKey: z.string().max(400).optional(),
  })
  .refine((value) => Boolean(value.brief) !== Boolean(value.sourceAssetKey), {
    message: 'brief ya da sourceAssetKey — tam olarak biri',
    path: ['brief'],
  });
export type StudioModelCreateInput = z.infer<typeof studioModelCreateSchema>;

export interface StudioModelCreateResponse {
  model: StudioModelCard;
  /// Yükleme yolunda 0 — sağlayıcı çağrısı yok.
  reservedCredits: number;
}
